<script setup lang="ts">
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import camelCase from 'lodash/camelCase.js';
import bbIcons from '@assets/bb-icons/index.js';
import { computed } from 'vue';
import type { IconColors, IconType } from '@components/icons/icon-types.js';
import { DisabledPropsInterface, useDisableComposable } from '@src/application/composables/disabled-composable.js';

const { disabled } = useDisableComposable();

const emit = defineEmits<(e: 'click', event: Event) => void>();

const props = withDefaults(defineProps<DisabledPropsInterface &{
    source?: string,
    icon: IconType,
    iconColor?: IconColors,
    hoverIconColor?: IconColors,
}>(), {
    source: 'font-awesome',
    iconColor: 'default',
});

const bbIconSvg = computed(() => {
    if (props.source !== 'bb') {
        return null;
    }
    return bbIcons[props.icon as 'kids'|'men'|'women'|'print-barcode'];
});
</script>

<script lang="ts">
import { library as fontAwesomeIconLibrary } from '@fortawesome/fontawesome-svg-core';
import { fontAwesomeIcons } from '@components/icons/icon-util.js';

fontAwesomeIconLibrary.add(...fontAwesomeIcons);

export default {
    inheritAttrs: false,
};
</script>

<template>
    <!--    Wir wollen im Icon den 'click' nur emittieren, wenn das Icon nicht disabled ist. Icons sind potentiell klickbare Elemente (zB. Icon-Tooltip), mit Actions die disabled gesetzt werden können sollen    -->
    <FontAwesomeIcon
        v-if="source === 'font-awesome'"
        v-bind="$attrs"
        :icon="icon"
        :class="[
            $style.icon,
            iconColor !== 'default' ? $style[iconColor + '_color'] : '',
            hoverIconColor ? $style[hoverIconColor + '_hover'] : iconColor !== 'default' ? $style[iconColor + '_hover'] : '',
        ]"
        @click="!disabled ? emit('click', $event) : null"
    />
    <span
        v-else-if="source === 'bb'"
        :class="[
            $style.bb_icon,
            iconColor !== 'default' ? $style[iconColor + '_color'] : '',
            hoverIconColor ? $style[hoverIconColor + '_hover'] : iconColor !== 'default' ? $style[iconColor + '_hover'] : '',
        ]"
        v-bind="$attrs"
        @click="!disabled ? emit('click', $event) : null"
        v-html="bbIconSvg"
    />
    <span
        v-else-if="source === 'custom'"
        v-bind="$attrs"
        :class="$style[camelCase('icons-custom-' + icon)]"
        @click="!disabled ? emit('click', $event) : null"
    />
</template>

<style lang="scss" module>
$default: #3d4e5c;
$primary: #17a8ac;
$red: #dc0e15;
$orange: #ff5000;
$yellow: #ffce10;
$green: #10bd4c;
$blue: #2691d9;
$violet: #a819ef;
$grey: #b9b9b9;
$white: #fff;

.bb_icon {
    display: inline-block;
    width: 1em;
    height: 1em;
    vertical-align: -0.125em;
}
.icon {
    color: $default;

    &.primary_color {
        color: $primary;
    }
    &.red_color {
        color: $red;
    }
    &.orange_color {
        color: $orange;
    }
    &.yellow_color {
        color: $yellow;
    }
    &.green_color {
        color: $green;
    }
    &.blue_color {
        color: $blue;
    }
    &.violet_color {
        color: $violet;
    }
    &.grey_color {
        color: $grey;
    }
    &.white_color {
        color: $white;
    }
    &.inherit_color {
        color: inherit;
    }
    &:hover {
        color: $default;

        &.primary_hover {
            color: $primary;
        }
        &.red_hover {
            color: $red;
        }
        &.orange_hover {
            color: $orange;
        }
        &.yellow_hover {
            color: $yellow;
        }
        &.green_hover {
            color: $green;
        }
        &.blue_hover {
            color: $blue;
        }
        &.grey_hover {
            color: $grey;
        }
        &.white_hover {
            color: $white;
        }
        &.inherit_hover {
            color: inherit;
        }
    }
}
</style>
